import { motion } from "framer-motion";

function TransportIllustration() {
  const zones = [
    // Teal — left column (Express)
    { x: 8, y: 8, w: 122, h: 112, rx: 8, fill: "hsl(186 100% 18.4% / 0.12)", stroke: "hsl(186 100% 18.4% / 0.4)", postcode: "411 01", delay: 0.3 },
    { x: 8, y: 124, w: 122, h: 112, rx: 8, fill: "hsl(186 100% 18.4% / 0.09)", stroke: "hsl(186 100% 18.4% / 0.32)", postcode: "411 05", delay: 0.4 },
    // Coral — middle column (Standard)
    { x: 134, y: 8, w: 130, h: 112, rx: 8, fill: "hsl(0 100% 70% / 0.09)", stroke: "hsl(0 100% 70% / 0.32)", postcode: "411 10", delay: 0.5 },
    { x: 134, y: 124, w: 130, h: 112, rx: 8, fill: "hsl(0 100% 70% / 0.06)", stroke: "hsl(0 100% 70% / 0.25)", postcode: "411 15", delay: 0.55 },
    // Turkos — right column (Ekonomi)
    { x: 268, y: 8, w: 124, h: 112, rx: 8, fill: "hsl(181 100% 33% / 0.10)", stroke: "hsl(181 100% 33% / 0.32)", postcode: "411 20", delay: 0.6 },
    { x: 268, y: 124, w: 124, h: 112, rx: 8, fill: "hsl(181 100% 33% / 0.07)", stroke: "hsl(181 100% 33% / 0.25)", postcode: "411 25", delay: 0.65 },
  ];

  // Pins placed inside specific zones (offset from postcode)
  const deliveryPins = [
    { cx: 199, cy: 82, delay: 1.0 },   // zone 3 lower
    { cx: 330, cy: 82, delay: 1.3 },   // zone 5 lower
    { cx: 199, cy: 200, delay: 1.2 },  // zone 4 lower
    { cx: 330, cy: 200, delay: 1.6 },  // zone 6 lower
  ];

  return (
    <svg viewBox="0 0 400 300" fill="none" className="w-full h-full">
      {/* Zone rectangles */}
      {zones.map((zone, i) => (
        <motion.g key={`zone-${i}`}>
          <motion.rect
            x={zone.x} y={zone.y} width={zone.w} height={zone.h} rx={zone.rx}
            fill={zone.fill} stroke={zone.stroke} strokeWidth={1.5}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: zone.delay }}
          />
          <motion.text
            x={zone.x + zone.w / 2} y={zone.y + 24}
            textAnchor="middle" fontSize="9" fontWeight="500"
            fill={zone.stroke}
            initial={{ opacity: 0 }} animate={{ opacity: 0.7 }}
            transition={{ delay: zone.delay + 0.3 }}
          >
            {zone.postcode}
          </motion.text>
        </motion.g>
      ))}

      {/* Route path through zones */}
      <motion.path
        d="M 69 200 C 69 160, 140 130, 199 82 S 290 50, 330 82"
        stroke="hsl(0 100% 70%)"
        strokeWidth={2.5}
        strokeDasharray="8 4"
        fill="none"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={{ duration: 1.8, ease: "easeInOut", delay: 0.8 }}
      />

      {/* Destination pins */}
      {deliveryPins.map((pin, i) => (
        <motion.g key={`pin-${i}`}>
          <motion.ellipse
            cx={pin.cx} cy={pin.cy + 12}
            rx={8} ry={3}
            fill="hsl(186 100% 18.4% / 0.1)"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: pin.delay + 0.1, duration: 0.3 }}
          />
          <motion.path
            d={`M ${pin.cx} ${pin.cy - 18} C ${pin.cx - 10} ${pin.cy - 18}, ${pin.cx - 10} ${pin.cy - 4}, ${pin.cx} ${pin.cy + 6} C ${pin.cx + 10} ${pin.cy - 4}, ${pin.cx + 10} ${pin.cy - 18}, ${pin.cx} ${pin.cy - 18}`}
            fill="hsl(0 100% 70%)"
            initial={{ scale: 0, y: -20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 15, delay: pin.delay }}
          />
          <motion.circle
            cx={pin.cx} cy={pin.cy - 10}
            r={3} fill="hsl(0 0% 100%)"
            initial={{ scale: 0 }} animate={{ scale: 1 }}
            transition={{ delay: pin.delay + 0.2, duration: 0.2 }}
          />
        </motion.g>
      ))}

      {/* Origin warehouse — inside zone 2 */}
      <motion.g
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
      >
        <rect x={49} y={190} width={40} height={24} rx={4} fill="hsl(186 100% 18.4%)" />
        <rect x={57} y={196} width={8} height={8} rx={1} fill="hsl(186 100% 18.4% / 0.3)" stroke="hsl(0 0% 100% / 0.5)" strokeWidth={0.5} />
        <rect x={69} y={196} width={8} height={8} rx={1} fill="hsl(186 100% 18.4% / 0.3)" stroke="hsl(0 0% 100% / 0.5)" strokeWidth={0.5} />
        <path d="M 49 190 L 69 178 L 89 190" fill="hsl(186 100% 18.4% / 0.8)" />
      </motion.g>

      {/* Moving truck indicator */}
      <motion.circle
        cx={0} cy={0} r={5}
        fill="hsl(186 100% 18.4%)" stroke="hsl(0 0% 100%)" strokeWidth={1.5}
        initial={{ offsetDistance: "0%" }}
        animate={{ offsetDistance: "100%" }}
        style={{
          offsetPath: `path("M 69 200 C 69 160, 140 130, 199 82 S 290 50, 330 82")`,
        }}
        transition={{ duration: 3, ease: "easeInOut", delay: 1.0, repeat: Infinity, repeatDelay: 2 }}
      />

      {/* Legend */}
      {[
        { cx: 90, label: "Express", color: "hsl(186 100% 18.4%)" },
        { cx: 200, label: "Standard", color: "hsl(0 100% 70%)" },
        { cx: 310, label: "Ekonomi", color: "hsl(181 100% 33%)" },
      ].map((item, i) => (
        <motion.g key={`legend-${i}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.0 + i * 0.1 }}>
          <circle cx={item.cx - 20} cy={268} r={5} fill={item.color} />
          <text x={item.cx - 10} y={272} fontSize="10" fontWeight="500" fill="hsl(186 100% 18.4% / 0.6)">
            {item.label}
          </text>
        </motion.g>
      ))}
    </svg>
  );
}

function ServiceIllustration() {
  const zones = [
    // Teal — left column (Servicezon 1)
    { x: 8, y: 8, w: 122, h: 112, rx: 8, fill: "hsl(186 100% 18.4% / 0.14)", stroke: "hsl(186 100% 18.4% / 0.42)", postcode: "752 20", delay: 0.3 },
    { x: 8, y: 124, w: 122, h: 112, rx: 8, fill: "hsl(186 100% 18.4% / 0.09)", stroke: "hsl(186 100% 18.4% / 0.32)", postcode: "752 25", delay: 0.45 },
    // Coral — middle column (Servicezon 2)
    { x: 134, y: 8, w: 130, h: 112, rx: 8, fill: "hsl(0 100% 70% / 0.10)", stroke: "hsl(0 100% 70% / 0.35)", postcode: "753 10", delay: 0.5 },
    { x: 134, y: 124, w: 130, h: 112, rx: 8, fill: "hsl(0 100% 70% / 0.07)", stroke: "hsl(0 100% 70% / 0.25)", postcode: "753 15", delay: 0.55 },
    // Turkos — bottom column (Servicezon 3)
    { x: 268, y: 8, w: 124, h: 112, rx: 8, fill: "hsl(181 100% 33% / 0.10)", stroke: "hsl(181 100% 33% / 0.32)", postcode: "754 50", delay: 0.6 },
    { x: 268, y: 124, w: 124, h: 112, rx: 8, fill: "hsl(181 100% 33% / 0.07)", stroke: "hsl(181 100% 33% / 0.25)", postcode: "754 55", delay: 0.65 },
  ];

  // Service points centered in specific zones
  const servicePoints = [
    { x: 69, y: 80, icon: "🔧", delay: 0.9 },
    { x: 199, y: 80, icon: "📍", delay: 1.1 },
    { x: 330, y: 200, icon: "👤", delay: 1.3 },
    { x: 69, y: 200, icon: "🔧", delay: 1.5 },
  ];

  const connections = [
    { x1: 69, y1: 80, x2: 199, y2: 80 },
    { x1: 199, y1: 80, x2: 330, y2: 200 },
    { x1: 69, y1: 80, x2: 69, y2: 200 },
    { x1: 69, y1: 200, x2: 330, y2: 200 },
  ];

  return (
    <svg viewBox="0 0 400 300" fill="none" className="w-full h-full">
      {/* Zone rectangles */}
      {zones.map((zone, i) => (
        <motion.g key={`zone-${i}`}>
          <motion.rect
            x={zone.x} y={zone.y} width={zone.w} height={zone.h} rx={zone.rx}
            fill={zone.fill} stroke={zone.stroke} strokeWidth={1.5}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: zone.delay }}
          />
          <motion.text
            x={zone.x + zone.w / 2} y={zone.y + 24}
            textAnchor="middle" fontSize="9" fontWeight="500"
            fill={zone.stroke}
            initial={{ opacity: 0 }} animate={{ opacity: 0.6 }}
            transition={{ delay: zone.delay + 0.3 }}
          >
            {zone.postcode}
          </motion.text>
        </motion.g>
      ))}

      {/* Connection lines between service points */}
      {connections.map((line, i) => (
        <motion.line
          key={`conn-${i}`}
          x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2}
          stroke="hsl(0 100% 70% / 0.25)" strokeWidth={1} strokeDasharray="3 3"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.7 + i * 0.1 }}
        />
      ))}

      {/* Service points */}
      {servicePoints.map((pt, i) => (
        <motion.g key={`tool-${i}`}>
          <motion.circle
            cx={pt.x} cy={pt.y} r={14}
            fill="hsl(0 0% 100%)" stroke="hsl(186 100% 18.4% / 0.3)" strokeWidth={1.5}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 250, damping: 15, delay: pt.delay }}
            style={{ transformOrigin: `${pt.x}px ${pt.y}px` }}
          />
          <motion.text
            x={pt.x} y={pt.y + 5}
            textAnchor="middle" fontSize="12"
            initial={{ scale: 0 }} animate={{ scale: 1 }}
            transition={{ delay: pt.delay + 0.15, duration: 0.2 }}
            style={{ transformOrigin: `${pt.x}px ${pt.y + 5}px` }}
          >
            {pt.icon}
          </motion.text>
        </motion.g>
      ))}

      {/* Legend */}
      {[
        { cx: 70, label: "Servicezon 1", color: "hsl(186 100% 18.4%)" },
        { cx: 185, label: "Servicezon 2", color: "hsl(0 100% 70%)" },
        { cx: 310, label: "Servicezon 3", color: "hsl(181 100% 33%)" },
      ].map((item, i) => (
        <motion.g key={`legend-${i}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.0 + i * 0.1 }}>
          <circle cx={item.cx - 25} cy={268} r={5} fill={item.color} />
          <text x={item.cx - 15} y={272} fontSize="9" fontWeight="500" fill="hsl(186 100% 18.4% / 0.6)">
            {item.label}
          </text>
        </motion.g>
      ))}
    </svg>
  );
}

function EcommerceIllustration() {
  return (
    <svg viewBox="0 0 400 300" fill="none" className="w-full h-full">
      {/* Address input field */}
      <motion.g
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <rect x={60} y={40} width={280} height={44} rx={8} fill="hsl(0 0% 100%)" stroke="hsl(186 100% 18.4% / 0.2)" strokeWidth={1.5} />
        <text x={76} y={67} fontSize="13" fill="hsl(190 8% 45%)">
          Sök adress...
        </text>
      </motion.g>

      {/* Typing animation */}
      <motion.g
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
      >
        <rect x={60} y={40} width={280} height={44} rx={8} fill="hsl(0 0% 100%)" stroke="hsl(0 100% 70% / 0.5)" strokeWidth={2} />
        <motion.text
          x={76}
          y={67}
          fontSize="13"
          fontWeight="500"
          fill="hsl(190 15% 15%)"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.0 }}
        >
          Storgatan 12, Sto
        </motion.text>
        {/* Cursor */}
        <motion.rect
          x={182}
          y={52}
          width={2}
          height={18}
          fill="hsl(0 100% 70%)"
          animate={{ opacity: [1, 0, 1] }}
          transition={{ repeat: Infinity, duration: 0.8 }}
        />
      </motion.g>

      {/* Autocomplete dropdown */}
      <motion.g
        initial={{ opacity: 0, y: -5 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.3, duration: 0.3 }}
      >
        <rect x={60} y={88} width={280} height={80} rx={8} fill="hsl(0 0% 100%)" stroke="hsl(186 100% 18.4% / 0.15)" strokeWidth={1} filter="drop-shadow(0 4px 12px rgba(0,0,0,0.08))" />
        
        {/* Option 1 - highlighted */}
        <rect x={64} y={92} width={272} height={34} rx={4} fill="hsl(186 100% 18.4% / 0.06)" />
        <text x={76} y={113} fontSize="12" fontWeight="500" fill="hsl(190 15% 15%)">
          Storgatan 12, 114 51 Stockholm
        </text>
        
        {/* Option 2 */}
        <text x={76} y={147} fontSize="12" fill="hsl(190 8% 45%)">
          Storgatan 12, 582 23 Linköping
        </text>
      </motion.g>

      {/* Validation checkmark */}
      <motion.g
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 15,
          delay: 1.8,
        }}
      >
        <circle cx={320} cy={62} r={14} fill="hsl(140 60% 45%)" />
        <path d="M 312 62 L 317 67 L 328 56" stroke="hsl(0 0% 100%)" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </motion.g>

      {/* Package delivery visualization */}
      <motion.g
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2 }}
      >
        {/* Arrow from form to pin */}
        <motion.path
          d="M 200 175 C 200 200, 200 210, 200 230"
          stroke="hsl(0 100% 70% / 0.3)"
          strokeWidth={1.5}
          strokeDasharray="4 4"
          fill="none"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.8, delay: 2.3 }}
        />

        {/* Destination pin */}
        <motion.g
          initial={{ scale: 0, y: -15 }}
          animate={{ scale: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 250, damping: 12, delay: 2.6 }}
        >
          <ellipse cx={200} cy={268} rx={12} ry={4} fill="hsl(186 100% 18.4% / 0.1)" />
          <path d="M 200 235 C 188 235, 185 248, 200 262 C 215 248, 212 235, 200 235" fill="hsl(0 100% 70%)" />
          <circle cx={200} cy={246} r={4} fill="hsl(0 0% 100%)" />
        </motion.g>
      </motion.g>

      {/* Package icon */}
      <motion.g
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.4, duration: 0.4 }}
      >
        <rect x={105} y={195} width={30} height={26} rx={3} fill="hsl(186 100% 18.4%)" />
        <line x1={120} y1={195} x2={120} y2={221} stroke="hsl(0 0% 100% / 0.4)" strokeWidth={1} />
        <line x1={105} y1={208} x2={135} y2={208} stroke="hsl(0 0% 100% / 0.4)" strokeWidth={1} />
        
        {/* Checkmark on package */}
        <motion.g
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 2.8, type: "spring", stiffness: 300 }}
        >
          <circle cx={135} cy={195} r={8} fill="hsl(140 60% 45%)" />
          <path d="M 130 195 L 133 198 L 140 191" stroke="hsl(0 0% 100%)" strokeWidth={1.5} strokeLinecap="round" fill="none" />
        </motion.g>
      </motion.g>
    </svg>
  );
}

interface SegmentIllustrationProps {
  type: "transport" | "service" | "ecommerce";
}

export function SegmentIllustration({ type }: SegmentIllustrationProps) {
  switch (type) {
    case "transport":
      return <TransportIllustration />;
    case "service":
      return <ServiceIllustration />;
    case "ecommerce":
      return <EcommerceIllustration />;
  }
}
